import './StudioView.scss';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import BookingForm from './BookingForm';
import queryString from 'query-string';
import {
  Hidden,
  Fab,
  Link,
  Paper,
  Divider,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  CardActions,
  IconButton,
  Grid,
  Typography,
  Avatar,
  Collapse,
  Button,
  Chip,
  SnackbarContent,
  Snackbar
} from '@material-ui/core';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import CalendarToday from '@material-ui/icons/CalendarToday';
import DateRange from '@material-ui/icons/DateRange';
import Timer from '@material-ui/icons/Timer';
import PinDrop from '@material-ui/icons/PinDrop';
import AddIcon from '@material-ui/icons/Add';
import Notifications from '@material-ui/icons/Notifications';
import StudioContactWidget from '../../routes/Studio/StudioContactWidget';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import moment from 'moment';
import { RouteComponentProps } from 'react-router-dom';

const styles = (theme: Theme) => createStyles({
  typography: {
    padding: theme.spacing(2),
  },
  card: {
    display: 'flex',
  },
  media: {
    height: 0,
    paddingTop: '56.25%', // 16:9
  },
  cover: {
    width: 150,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  schedule: {
    maxWidth: theme.spacing(170),
    margin: `0 auto`,
    padding: `0 ${theme.spacing(2)}px`,
  },
  rooms: {
    margin: theme.spacing(1),
    padding: `${theme.spacing(2)}px 0`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: theme.spacing(15),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  coursesGrid: {
    paddingTop: theme.spacing(2),
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  buttons: {
    '& > *': {
      flex: 1,
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
  studioCard: {},
  studioCover: {
    height: 0,
    paddingTop: '56.25%', // 16:9
  },
  bigAvatar: {
    width: 150,
    height: 150,
  },
  timeSelect: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  alternativeCourses: {
    marginTop: theme.spacing(2),
  },
  fab: {
    position: 'fixed',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 100,
  },
  avatar: {}
});

interface Studio {
  _id: string;
  id: string;
  owner_id: string;
  name: string;
  avatar?: string;
  roomsMap?: Record<string, Room>;
  pricing?: string;
  address?: string;
  bookFrom: number;
  bookTo: number;
  domainName?: string;
  [key: string]: any;
}

interface Room {
  name: string;
  [key: string]: any;
}

interface Owner {
  _id: string;
  timezone: string;
  [key: string]: any;
}

interface Course {
  _id: string;
  title: string;
  avatar?: string;
  description?: string;
  billingTag: string;
  room: string[];
  private: boolean;
  allowBookEmptySlots: boolean;
  allowMultiBooking: boolean;
  beforeBookingText?: string;
  [key: string]: any;
}

interface FreeSlot {
  classDateTime: string;
  teacherId: string[] | string;
  groupId: string;
  roomName?: string[];
  [key: string]: any;
}

interface Teacher {
  _id: string;
  name: string;
  avatar?: string;
  [key: string]: any;
}

interface AlternativeCourseData {
  course: Course;
  teacher?: Teacher;
  classDates: FreeSlot[];
}

interface RouteParams {
  courseId?: string;
  [key: string]: string | undefined;
}

interface MultipleGroupBookingViewProps extends WithStyles<typeof styles>, WithSnackbarProps, Partial<RouteComponentProps<RouteParams>> {
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  domain: {
    id: string;
    [key: string]: any;
  };
  studio: Studio;
  course: Course;
  owner: Owner;
  date: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  events?: FreeSlot[];
  alternativeCourses?: AlternativeCourseData[];
  registerToClass: (studio: Studio, classId: string) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new?: boolean }) => void;
  toMyClasses: (ownerId: string, studioClientId: string, date: string, numberOfSlots: number) => void;
}

interface MultipleGroupBookingViewState {
  studio?: Studio;
  owner?: Owner;
  notifications: any[];
  showFullDescription: boolean;
  showFullPricing: boolean;
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  repeatableClass?: any;
  categoryLinks: any[];
  reviews: any[];
  coursesExpanded: Record<string, boolean>;
  courseEvents: Record<string, any>;
  to: moment.Moment;
  minDate: moment.Moment;
  maxDate: moment.Moment;
  date: moment.Moment;
  selectedEvents: Record<string, FreeSlot>;
  events?: FreeSlot[];
  alternativeCourses?: AlternativeCourseData[];
  course?: Course;
  teacher?: Teacher;
  showAlternatives?: boolean;
  loadingAlternatives?: boolean;
  showPricingDetails?: boolean;
  showBookingForm?: boolean;
}

class MultipleGroupBookingView extends React.Component<MultipleGroupBookingViewProps, MultipleGroupBookingViewState> {
  constructor(props: MultipleGroupBookingViewProps) {
    super(props);
    const pageState = (window as any).__PAGE_STATE__;
    
    this.state = {
      studio: pageState && pageState.studio,
      owner: pageState && pageState.studioOwner,
      notifications: [],
      showFullDescription: false,
      showFullPricing: false,
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      repeatableClass: undefined,
      categoryLinks: [],
      reviews: [],
      coursesExpanded: {},
      courseEvents: {},
      to: moment.utc().add(1, 'days').endOf('isoWeek'),
      minDate: props.minDate,
      maxDate: props.maxDate,
      date: props.date,
      selectedEvents: {},
      events: props.events,
      alternativeCourses: props.alternativeCourses,
    };
  }
  
  componentDidMount() {
    if (!this.props.alternativeCourses) {
      this.loadAltrnatives(
        this.props.studio,
        this.props.course,
        this.props.date,
        this.props.owner
      );
    }
  }

  loadEvents = (studio: Studio, courseId: string, date: moment.Moment, owner: Owner) => {
    this.setState({
      events: undefined,
      alternativeCourses: undefined,
      selectedEvents: {},
    });
    serviceLocator.registrationApi
      .getFreeSlotsOnCourse(studio._id, courseId, date, owner.timezone)
      .then((events: { classDates: FreeSlot[]; studio: Studio; course: Course; teacher: Teacher }) => {
        let freeSlots = events.classDates;

        let changeSet: Partial<MultipleGroupBookingViewState> = { events: freeSlots };
        if (freeSlots && freeSlots.length <= 2) {
          changeSet.showAlternatives = true;
        } else {
          changeSet.showAlternatives = false;
        }

        if (!this.state.teacher) {
          changeSet.teacher = events.teacher;
        }
        this.setState(changeSet as MultipleGroupBookingViewState);
        if (this.props.course) {
          this.loadAltrnatives(
            studio,
            this.props.course,
            date,
            this.props.owner
          );
        }
      });
  };
  
  loadAltrnatives = (studio: Studio, course: Course, date: moment.Moment, owner: Owner) => {
    this.setState({ loadingAlternatives: true });
    serviceLocator.registrationApi
      .getAlternativeFreeSlots(
        studio._id,
        course.billingTag,
        course._id,
        date,
        owner.timezone
      )
      .then((alternativeCourses: AlternativeCourseData[]) => {
        this.setState({
          alternativeCourses: alternativeCourses,
          loadingAlternatives: false,
        });
      }, LayoutHelper.onErr);
  };

  onSuccess = (studio: Studio) => {
    this.loadOwnerInfo(studio);
  };
  
  loadOwnerInfo = (studio: Studio) => {
    serviceLocator.userApi.getOwnerInfo(studio.owner_id).then((owner: Owner) => {
      this.setState({ owner: owner, studio: studio });
      this.initDates(studio, owner);
    }, LayoutHelper.onErr);
  };
  
  initDates = (studio: Studio, owner: Owner) => {
    let date = this.props.date;
    const utcOffset = moment.tz(owner.timezone).utcOffset() / 60;
    let minDate = moment
      .utc()
      .add(studio.bookFrom + utcOffset, 'hours')
      .startOf('day');
    let maxDate = moment
      .utc()
      .add(studio.bookTo + utcOffset, 'hours')
      .startOf('day');
    if (date.diff(minDate) < 0 || date.diff(maxDate) > 0) {
      date = minDate;
    }
    this.setState({ date: date, minDate: minDate, maxDate: maxDate });
    
    if (this.props.match?.params.courseId) {
      this.loadEvents(studio, this.props.match.params.courseId, date, owner);
    }
  };

  render() {
    let { classes } = this.props;

    if (this.state.showBookingForm) {
      return (
        <Grid container spacing={1} direction="row-reverse" id="booking-form">
          <Grid item xs={12} md={4}>
            <Grid container spacing={1} justify="center">
              <Grid item xs={12}>
                <Typography>
                  {this.props.translate('studio.class.signUpConfirm')}
                </Typography>
              </Grid>
              {Object.entries(this.state.selectedEvents)
                .sort((a, b) => {
                  return a[1].classDateTime.localeCompare(b[1].classDateTime);
                })
                .map(([key, event]) => {
                  let classDateTime = moment.utc(event.classDateTime);
                  return (
                    <Grid item key={key}>
                      <Chip
                        color="secondary"
                        label={
                          <Fragment>
                            {event.roomName && event.roomName.join(' ')}&nbsp;
                            {classDateTime.format('ddd DD MMM HH:mm')}
                          </Fragment>
                        }
                        onDelete={() => {
                          let selectedEvents = this.state.selectedEvents;
                          delete selectedEvents[event.groupId];
                          let showBookingForm =
                            Object.keys(selectedEvents).length > 0;
                          this.setState({
                            selectedEvents: selectedEvents,
                            showBookingForm: showBookingForm,
                          });
                        }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <BookingForm
              studio={this.props.studio}
              domain={this.props.domain}
              buttonTitle={this.props.translate('studio.class.signUpSlots', {
                numberOfSlots: Object.values(this.state.selectedEvents).length,
                date: this.state.date.format('DD MMM'),
              })}
              onSubmit={async (values: any) => {
                try {
                  let slots = Object.values(this.state.selectedEvents);
                  const attendanceData =
                    await serviceLocator.registrationApi.createFreeSlots(
                      this.props.studio.owner_id,
                      this.props.studio._id,
                      slots,
                      values
                    );
                  const studioClient = attendanceData.studioClient;
                  const dateStr = this.state.date.format('DD-MM-YYYY');
                  this.props.toMyClasses(
                    this.props.studio.owner_id,
                    studioClient._id,
                    dateStr,
                    slots.length
                  );
                  this.props.enqueueSnackbar(
                    this.props.translate('studio.class.success', {
                      numberOfSlots: slots.length,
                      date: dateStr,
                    }),
                    {
                      variant: 'info',
                      anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                      persist: true,
                    }
                  );
                } catch (e) {
                  this.props.enqueueSnackbar(
                    this.props.translate('manageRegistration.bookingError'),
                    {
                      variant: 'error',
                      anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                      },
                      persist: true,
                    }
                  );
                  this.setState({ showBookingForm: false });
                  this.loadEvents(
                    this.props.studio,
                    this.props.course._id,
                    this.state.date,
                    this.props.owner
                  );
                }
              }}
              owner={this.props.owner}
            />
          </Grid>
        </Grid>
      );
    }
    
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          {this.renderTimeSelect()}
        </Grid>
        <Divider />
        {this.renderNoEvents()}
        {this.renderClasses(this.props.course)}
        {this.renderAlternativeCourses()}
        <Grid item xs={12}>
          <Fab
            color="primary"
            className={this.props.classes.fab}
            variant="extended"
            onClick={() => {
              this.setState({ showBookingForm: true });
              const element = document.querySelector('#booking-form');
              if (element) {
                element.scrollIntoView({
                  behavior: 'smooth',
                });
              }
            }}
            disabled={Object.keys(this.state.selectedEvents).length === 0}
          >
            {this.props.translate('studio.class.signUpSlots', {
              numberOfSlots: Object.values(this.state.selectedEvents).length,
              date: this.state.date.format('DD MMM'),
            })}
          </Fab>
        </Grid>
      </Grid>
    );
  }

  renderNoEvents = () => {
    if (this.state.events && !this.state.loadingAlternatives) {
      if (
        this.state.events.length === 0 &&
        this.state.alternativeCourses &&
        this.state.alternativeCourses.length === 0
      ) {
        return (
          <Grid item xs={12}>
            <Typography>
              {this.props.translate('manageRegistration.noClassesAvailable')}
            </Typography>
          </Grid>
        );
      }
    }
    return null;
  };

  renderTimeSelect = () => {
    let course = this.props.course;
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return (
          <Fragment>
            <Paper className={this.props.classes.timeSelect}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                  <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
                    <DatePicker
                      leftArrowIcon={<KeyboardArrowLeft />}
                      rightArrowIcon={<KeyboardArrowRight />}
                      keyboardIcon={<CalendarToday />}
                      dateRangeIcon={<DateRange />}
                      timeIcon={<Timer />}
                      minDate={this.props.minDate}
                      maxDate={this.props.maxDate}
                      format="DD MMM YYYY"
                      autoOk
                      inputVariant="outlined"
                      label={this.props.translate(
                        'manageRegistration.classDate'
                      )}
                      variant="inline"
                      value={this.state.date}
                      onChange={(date) => {
                        if (date) {
                          this.setState({ date: date });
                          this.loadEvents(
                            this.props.studio,
                            this.props.course._id,
                            date,
                            this.props.owner
                          );
                        }
                      }}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item>
                  <Typography>
                    {this.props.translate(
                      'manageRegistration.hostTimeZoneNotification'
                    )}
                    :{' '}
                    {this.props.owner &&
                      moment.tz(this.props.owner.timezone).format('Z z')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Fragment>
        );
      }
    }
    return null;
  };
  
  renderAlternativeCourses = () => {
    if (this.state.loadingAlternatives) {
      return LayoutHelper.renderLoader();
    }
    if (
      this.state.alternativeCourses &&
      this.state.alternativeCourses.length > 0
    ) {
      return (
        <Grid item xs={12}>
          <Grid
            container
            spacing={1}
            className={this.props.classes.alternativeCourses}
          >
            {this.state.alternativeCourses.map((alternativeCourse, index) => {
              return this.renderAlternativeCourse(alternativeCourse, index);
            })}
          </Grid>
        </Grid>
      );
    }
    return null;
  };
  
  renderAlternativeCourse = (alternativeCourseData: AlternativeCourseData, index: number) => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    if (classDates) {
      return (
        <Grid item xs={12} key={index}>
          <Card>
            {teacher && (
              <CardHeader
                avatar={<Avatar src={teacher.avatar} />}
                title={teacher.name}
              ></CardHeader>
            )}
            {this.renderCourseRoom(course)}
            <Divider />
            <CardContent>
              {classDates.length === 0 ? (
                <Typography>
                  {this.props.translate(
                    'manageRegistration.noClassesAvailable'
                  )}
                </Typography>
              ) : (
                classDates.map((freeSlot, idx) => {
                  let classDateTime = moment.utc(freeSlot.classDateTime);

                  return (
                    <Chip
                      key={idx}
                      icon={
                        !this.state.selectedEvents[freeSlot.groupId] ? (
                          <AddIcon />
                        ) : undefined
                      }
                      label={classDateTime.format('HH:mm')}
                      color="secondary"
                      variant={
                        this.state.selectedEvents[freeSlot.groupId]
                          ? undefined
                          : 'outlined'
                      }
                      onClick={
                        !this.state.selectedEvents[freeSlot.groupId]
                          ? () => {
                              let selectedEvents = {...this.state.selectedEvents};
                              selectedEvents[freeSlot.groupId] = freeSlot;
                              this.setState({ selectedEvents: selectedEvents });
                            }
                          : undefined
                      }
                      onDelete={
                        this.state.selectedEvents[freeSlot.groupId]
                          ? () => {
                              let selectedEvents = {...this.state.selectedEvents};
                              delete selectedEvents[freeSlot.groupId];
                              this.setState({ selectedEvents: selectedEvents });
                            }
                          : undefined
                      }
                    />
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      );
    }
    return null;
  };
  
  goToAlternativeFreeSlot = (alternativeCourseData: AlternativeCourseData, freeSlot: FreeSlot) => {
    let course = alternativeCourseData.course;
    let teacher = alternativeCourseData.teacher;
    let classDates = alternativeCourseData.classDates;
    this.loadAltrnatives(
      this.props.studio,
      course,
      this.state.date,
      this.props.owner
    );
    this.setState({
      course: course,
      teacher: teacher,
      events: classDates,
      showAlternatives: false,
    });
  };

  renderRepeatableClass = (course?: Course) => {
    if (course) {
      if (!course.private && course.allowBookEmptySlots) {
        return this.renderCourseHeader(course);
      } else {
        return this.props.translate('manageRegistration.courseIsNotAvailable');
      }
    } else {
      return LayoutHelper.renderLoader();
    }
  };
  
  renderCourseHeader = (course: Course) => {
    return (
      <Card>
        <CardHeader
          avatar={
            <Avatar
              src={this.props.studio.avatar}
              className={this.props.classes.avatar}
            ></Avatar>
          }
          title={
            <Fragment>
              <Link href={routes.getStudioLink(this.props.studio)}>
                {' '}
                {this.props.studio.name}
              </Link>
              <StudioContactWidget studio={this.props.studio} />
            </Fragment>
          }
          subheader={this.props.studio.address}
        ></CardHeader>
        <CardHeader
          avatar={
            course.avatar && (
              <Avatar
                src={course.avatar}
                className={this.props.classes.bigAvatar}
              />
            )
          }
          title={course.title}
        ></CardHeader>

        <CardContent>{this.renderCourseDescription(course)}</CardContent>
        <CardActions>
          <Button
            size="small"
            color="secondary"
            onClick={() => {
              this.setState({
                showPricingDetails: !this.state.showPricingDetails,
              });
            }}
          >
            <Notifications />
            {this.props.translate('studio.pricingDetails')}
          </Button>
        </CardActions>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          <CardContent>
            <div
              dangerouslySetInnerHTML={{ __html: this.props.studio.pricing || '' }}
              className="text"
            />
          </CardContent>
        </Collapse>
      </Card>
    );
  };
  
  renderClasses = (course?: Course) => {
    if (!this.state.events || !course) {
      return LayoutHelper.renderLoader();
    }
    if (this.state.events.length === 0) {
      return null;
    }
    const teacher = this.state.teacher;

    return (
      <Grid item xs={12}>
        <Card>
          {teacher && (
            <CardHeader
              avatar={<Avatar src={teacher.avatar} />}
              title={teacher.name}
            ></CardHeader>
          )}
          {this.renderCourseRoom(course)}
          <Divider />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item>
                <Typography>
                  {this.state.events.map((event, idx) => {
                    let classDateTime = moment.utc(event.classDateTime);
                    return (
                      <Chip
                        key={idx}
                        icon={
                          !this.state.selectedEvents[event.groupId] ? (
                            <AddIcon />
                          ) : undefined
                        }
                        label={classDateTime.format('HH:mm')}
                        color="secondary"
                        variant={
                          this.state.selectedEvents[event.groupId]
                            ? undefined
                            : 'outlined'
                        }
                        onClick={
                          !this.state.selectedEvents[event.groupId]
                            ? () => {
                                let selectedEvents = {...this.state.selectedEvents};
                                selectedEvents[event.groupId] = event;
                                this.setState({
                                  selectedEvents: selectedEvents,
                                });
                              }
                            : undefined
                        }
                        onDelete={
                          this.state.selectedEvents[event.groupId]
                            ? () => {
                                let selectedEvents = {...this.state.selectedEvents};
                                delete selectedEvents[event.groupId];
                                this.setState({
                                  selectedEvents: selectedEvents,
                                });
                              }
                            : undefined
                        }
                      />
                    );
                  })}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };
  
  renderCourseRoom = (course: Course) => {
    const roomsMap = this.props.studio.roomsMap;
    if (course.room.length === 1 && roomsMap) {
      const roomId = course.room[0];
      if (roomsMap[roomId]) {
        return (
          <CardContent>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <PinDrop />
              </Grid>
              <Grid item>{roomsMap[roomId].name}</Grid>
            </Grid>
          </CardContent>
        );
      }
    }
    return null;
  };

  renderCourseDescription = (course: Course) => {
    if (course.description) {
      return (
        <Typography component="div">
          <div dangerouslySetInnerHTML={{ __html: course.description }} />
        </Typography>
      );
    }
    return null;
  };
}

function mapStateToProps(state: any, ownProps: MultipleGroupBookingViewProps) {
  return {
    translate: getTranslate(state.locale),
    currentLanguage: getActiveLanguage(state.locale).code,
    user: state.user,
    domain: state.domain.domain,
  };
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(
          push(`${routes.class()}?keyword=${''}&location=${''}`)
        );
      } else {
        dispatch(push(`${routes.classId()}?keyword=${''}`));
      }
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    clientAttendance: (studio: Studio, groupId: string, attendanceId: string, client: { new?: boolean }) => {
      dispatch(
        push(
          `${routes.clientAttendance({
            domainName: studio.domainName,
            groupId: groupId,
            id: attendanceId,
          })}${client.new ? '?new=true' : ''}`
        )
      );
    },
    toMyClasses: (ownerId: string, studioClientId: string, date: string, numberOfSlots: number) => {
      dispatch(
        push(
          `${routes.studioClientClasses({
            ownerId: ownerId,
            studioClientId: studioClientId,
          })}?from=${date}&signUps=${numberOfSlots}`
        )
      );
    },
  };
};

export default withSnackbar(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    withStyles(styles)(MultipleGroupBookingView)
  )
);